import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Flex, Form, Segmented, Tooltip, Typography } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../../../../../context/MachineDetailsRobotScheduleContext';
import { useScheduleUpsertModalCV50 } from '../../../../../../context/ScheduleCreateModalContext';
import { CLEANING_MODE_OPTIONS } from '../../../../../../types/RobotScheduleMap.types';
import type { CleaningZonesProps } from '../../../../../../types/CleaningZones.types';
import { useMapAreas } from '../../../../../../hooks/useMapAreas';
import { isCleaningZonesSelectionValid, toggleCleaningZone } from '../../../../../../utils/cleaningZones';
import { CleaningScheduleCardFormItem } from '../../CleaningScheduleCard.styles';
import { CleaningAreaList } from './CleaningAreaList/CleaningAreaList';
import { CleaningAreaMapOverlay } from './CleaningAreaMapOverlay/CleaningAreaMapOverlay';
import {
  CLEANING_AREA_SELECTION_MAX_HEIGHT,
  CleaningZonesAreaSelectionRow,
  CleaningZonesContainer,
  CleaningZonesErrorText,
  CleaningZonesHowToIcon,
} from './CleaningZones.styles';
import { CleaningModeEnum } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { FeatureFlagSelectors } from 'app/cross-cutting-concerns/feature-flags/state/featureFlagSelectors';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

export const CleaningZones = ({
  mapImageSrc,
  mapName,
  mapWork,
  mapWorkUrl,
  mapConfig,
  mapFunction,
  mapFunctionUrl,
  mapWidthPx,
  mapHeightPx,
}: CleaningZonesProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { isReadOnly } = useMachineDetailsRobotSchedule();
  const { form, handleCleaningZonesChange } = useScheduleUpsertModalCV50();

  // Map Area cleaning mode (CV50) is not released yet; it ships behind the
  // `ROBOT_SCHEDULE_MAP_AREA` next-EM-version flag. While it is off, the schedule
  // always cleans the whole map, so the entire cleaning-mode selector stays hidden.
  const isMapAreaEnabled = useSelector(FeatureFlagSelectors.selectFeatureFlagConfig).ROBOT_SCHEDULE_MAP_AREA;

  const cleaningMode = Form.useWatch('cleaningMode', form) ?? CleaningModeEnum.WholeMap;
  const cleaningZones: string[] = Form.useWatch('cleaningZones', form) ?? [];

  // Area list matches the map's rendered height (reported by the overlay); starts at max.
  const [mapRenderedHeightPx, setMapRenderedHeightPx] = useState(CLEANING_AREA_SELECTION_MAX_HEIGHT);

  // Hook still runs while hidden (rules of hooks), but empty inputs make it a no-op.
  const { areas, features } = useMapAreas({
    ...(isMapAreaEnabled && { mapWork, mapWorkUrl, mapConfig, mapFunction, mapFunctionUrl }),
    imageWidthPx: mapWidthPx,
    imageHeightPx: mapHeightPx,
  });

  if (!isMapAreaEnabled) {
    return null;
  }

  const handleToggleArea = (areaId: string): void => {
    handleCleaningZonesChange(toggleCleaningZone(cleaningZones, areaId));
  };

  const handleReorder = (nextAreaIds: string[]): void => {
    handleCleaningZonesChange(nextAreaIds);
  };

  // Leaving Area mode must clear any existing selection, so re-selecting Area later starts empty
  // (spec.md Acceptance Scenario 9) rather than resurrecting a stale selection/validation error.
  const handleCleaningModeChange = (nextCleaningMode: CleaningModeEnum): void => {
    if (isReadOnly) {
      return;
    }

    if (nextCleaningMode !== CleaningModeEnum.Area) {
      handleCleaningZonesChange([]);
    }
  };

  return (
    <CleaningZonesContainer vertical gap={kccThemeConstants.SPACING.XS}>
      <Typography.Text
        style={{
          fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
          lineHeight: kccThemeConstants.TYPOGRAPHY.LINE_HEIGHT.SM,
        }}
      >
        {t('createSchedule.cleaning')}
      </Typography.Text>

      <Flex align="center" justify="space-between">
        <CleaningScheduleCardFormItem name="cleaningMode">
          <Segmented<CleaningModeEnum>
            disabled={isReadOnly}
            options={CLEANING_MODE_OPTIONS.map(option => ({
              label: t(option.label),
              value: option.value,
            }))}
            onChange={(nextCleaningMode): void => {
              if (isReadOnly) {
                return;
              }

              handleCleaningModeChange(nextCleaningMode);
            }}
          />
        </CleaningScheduleCardFormItem>

        {cleaningMode === CleaningModeEnum.Area && (
          <Tooltip title={t('createSchedule.cleaningZonesHowTo')} trigger={['hover', 'focus']}>
            <Flex
              align="center"
              gap={kccThemeConstants.SPACING.XXS}
              tabIndex={0}
              style={{ cursor: 'default', flexShrink: 0 }}
            >
              <CleaningZonesHowToIcon name="question" />
              <Typography.Text
                style={{ fontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.BASE, whiteSpace: 'nowrap', paddingTop: 2 }}
              >
                {t('createSchedule.howTo')}
              </Typography.Text>
            </Flex>
          </Tooltip>
        )}
      </Flex>

      {cleaningMode === CleaningModeEnum.Area && (
        <CleaningZonesAreaSelectionRow>
          <CleaningAreaMapOverlay
            mapImageSrc={mapImageSrc}
            mapName={mapName}
            mapWidthPx={mapWidthPx}
            mapHeightPx={mapHeightPx}
            areas={areas}
            features={features}
            selectedAreaIds={cleaningZones}
            onToggleArea={handleToggleArea}
            disabled={isReadOnly}
            onRenderedHeightChange={setMapRenderedHeightPx}
          />

          <CleaningAreaList
            areas={areas}
            selectedAreaIds={cleaningZones}
            onToggleArea={handleToggleArea}
            onReorder={handleReorder}
            disabled={isReadOnly}
            heightPx={mapRenderedHeightPx}
          />
        </CleaningZonesAreaSelectionRow>
      )}

      {/* Hidden field: cleaningZones is written via form.setFieldValue by the area controls
          above, not through a cloned input's value/onChange. noStyle keeps it out of layout
          entirely; the rejection carries no message (our own error text below handles display).
          shouldUpdate re-reads getFieldError so the visible text only appears once antd has
          actually validated this field (i.e. after a submit attempt), not just because the
          current selection happens to be empty. */}
      <Form.Item
        name="cleaningZones"
        noStyle
        rules={[
          {
            validator: (_, value: string[]): Promise<void> =>
              isCleaningZonesSelectionValid(cleaningMode, value ?? []) ? Promise.resolve() : Promise.reject(),
          },
        ]}
      >
        <input type="hidden" />
      </Form.Item>

      <Form.Item noStyle shouldUpdate>
        {(): JSX.Element | null =>
          cleaningMode === CleaningModeEnum.Area && form.getFieldError('cleaningZones').length > 0 ? (
            <CleaningZonesErrorText>{t('createSchedule.selectAtLeastOneArea')}</CleaningZonesErrorText>
          ) : null
        }
      </Form.Item>
    </CleaningZonesContainer>
  );
};
